import useTranslate from "../../../../modules/local/useTranslate";
import React, {useMemo, useState} from "react";
import {notifyOnError} from "../../factory/createErrorEvent";
import useAsyncAction from "../../../../modules/asyncCache/useAsyncAction";
import {
    course_class_createAttendance_Api,
    course_class_deleteAttendance_Api,
    course_class_editAttendance_Api
} from "../../../../apis";
import {getResponseItem, NullAsDefault} from "../../../Shared";
import {NotificationActionTypes, successNotify} from "../../factory/createNotification";
import {showDeleteConfirmDialog} from "../../factory/createConfirmDialog";
import {createModalTitle} from "../../../../components/renderFunctions/createModalTitle";
import {getId} from "../../../../apis/model/base";
import {
    CourseClassAttendanceCreateForm,
    CourseClassAttendanceUpdateForm,
    CourseClassAttendantsForm,
    CourseClassStudentAttendanceStats
} from "../CourseClass/CourseClassAttendanceForm";
import {FullSideModalLayout} from "../../../../components/Modal/SideModalLayout";
import {Modal} from "antd";
import {FetchAttendanceById} from "../components/FetchEntity";
import LoadingBlock from "../../../Loading/LoadingBlock";
import {AttendanceActionTypes} from "../index";


const AttendanceUpdateForm = ({attendance_id, ...props}) => (
    <FetchAttendanceById id={attendance_id}>
        {({item, isLoading}) => {
            if (isLoading) {
                return <LoadingBlock/>
            }
            return (
                <CourseClassAttendanceUpdateForm
                    {...props}
                    initialValues={item}
                />
            )
        }}
    </FetchAttendanceById>
)

export const useCourseAttendances = ({course_class}) => {
    const t = useTranslate()

    const [refresh_token, set_refresh_token] = useState()

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const onCancel = () => {
        setAction({})
    }

    const onRefresh = () => {
        onCancel()
        set_refresh_token(Date.now())
    }

    const onError = notifyOnError(t)

    const {handleAsyncAction: onCreate, ...creatingAsync} = useAsyncAction({
        onError,
        apiInfo: course_class_createAttendance_Api,
        onSuccess: (result, data) => {
            const new_attendant = getResponseItem(data?.response)
            successNotify(NotificationActionTypes.CREATE, t)
            onRefresh()
            setAction({type: AttendanceActionTypes.DETAIL, value: new_attendant})
        },
    })

    const {handleAsyncAction: onUpdate, ...updatingAsync} = useAsyncAction({
        onError,
        apiInfo: course_class_editAttendance_Api,
        onSuccess: (result, data) => {
            successNotify(NotificationActionTypes.UPDATE, t)
            onCancel()
        },
    })

    const {handleAsyncAction: onDelete} = useAsyncAction({
        onError,
        apiInfo: course_class_deleteAttendance_Api,
        onSuccess: (result, data) => {
            successNotify(NotificationActionTypes.DELETE, t)
            onRefresh()
        },
    })

    const handleAction = (action, params) => {
        switch (action) {
            case AttendanceActionTypes.VIEW_BY_SUBJECT_FILTER:
            case AttendanceActionTypes.DETAIL:
            case AttendanceActionTypes.CREATE:
            case AttendanceActionTypes.UPDATE:
                setAction({
                    type: action,
                    value: params,
                })
                break
            case AttendanceActionTypes.DELETE:
                showDeleteConfirmDialog({
                    translate: t,
                    title: 'delete this lesson',
                    message: 'are you sure you want to delete this lesson',
                    onOk: () => {
                        onDelete({}, {id: params?.id})
                    },
                })
            default:
                break
        }
    }

    const modal = useMemo(() => {
        let params = {}
        let modal_params = {}
        let Component = NullAsDefault
        switch (action?.type) {
            case AttendanceActionTypes.CREATE:
                modal_params = {
                    title: createModalTitle('create attendance info'),
                    width: 600,
                }
                params = {
                    initialValues: {...(action?.value || {})},
                    isSubmitting: creatingAsync.isLoading,
                    onSubmit: (values) => {
                        onCreate(values, {id: getId(course_class)})
                    },
                }
                Component = CourseClassAttendanceCreateForm
                break
            case AttendanceActionTypes.UPDATE:
                const attendance_id = getId(action?.value)
                modal_params = {
                    title: createModalTitle('edit attendance info'),
                    width: 600,
                }
                params = {
                    attendance_id,
                    isSubmitting: updatingAsync.isLoading,
                    onSubmit: (values) => {
                        onUpdate(values, {id: attendance_id})
                    },
                }
                Component = AttendanceUpdateForm
                break
            case AttendanceActionTypes.DETAIL:
                return (
                    <FullSideModalLayout
                        right={true}
                        onCancel={onCancel}
                        title={createModalTitle('attendance check for a session')}>
                        <CourseClassAttendantsForm
                            item={course_class}
                            attendance={action?.value}
                        />
                    </FullSideModalLayout>
                )
                break
            case AttendanceActionTypes.VIEW_BY_SUBJECT_FILTER:
                return (
                    <FullSideModalLayout
                        right={true}
                        onCancel={onCancel}
                        title={createModalTitle('attendance statistics')}>
                        <CourseClassStudentAttendanceStats
                            courseClass={course_class}
                        />
                    </FullSideModalLayout>
                )
                break
            default:
                break
        }

        return (
            <Modal
                footer={null}
                onCancel={onCancel}
                open={action?.type}
                destroyOnHidden={true}
                className="custom-modal"
                {...modal_params}>
                <Component
                    {...{onCancel}}
                    {...params}
                    course_class={course_class}
                />
            </Modal>
        )
    }, [action, course_class, creatingAsync.isLoading, updatingAsync.isLoading])

    return {
        modal,
        action,
        handleAction,
        refresh_token,
    }
}